import { Environment, Lightformer } from '@react-three/drei';
import { memo } from 'react';

const BASE_CYCLORAMA_FLOOR_REFLECTION_INTENSITY = 0.3;
const BASE_CYCLORAMA_WALL_REFLECTION_INTENSITY = 0.04;
const MAX_CYCLORAMA_FLOOR_REFLECTION_INTENSITY = 8;
const MAX_CYCLORAMA_WALL_REFLECTION_INTENSITY = 2;
const CYCLORAMA_REFLECTION_BOOST_EXPONENT = 6;
const STUDIO_ENVIRONMENT_RESOLUTION = 512;
const CYCLORAMA_WALL_POSITIONS:Array<[number, number, number]> = [
    [0, 1, -10],
    [0, 1, 10],
    [-10, 1, 0],
    [10, 1, 0],
];

interface Props {
    cycloramaColor: string
    reflectionStrength: number
}

function getCycloramaReflectionIntensity(
    strength:number,
    baseIntensity:number,
    maxIntensity:number,
):number {
    const boostedStrength = strength ** CYCLORAMA_REFLECTION_BOOST_EXPONENT;

    return strength * baseIntensity
        + boostedStrength * (maxIntensity - baseIntensity);
}

/**
 * Reflection-only environment. Neutral panels shape the product, while the
 * colored floor and wall panels approximate cyclorama reflections. The upper
 * slider range is deliberately nonlinear and overdriven.
 */
export default memo(function StudioEnvironment({
    cycloramaColor,
    reflectionStrength,
}:Props) {
    return <Environment
        frames={1}
        resolution={STUDIO_ENVIRONMENT_RESOLUTION}
    >
        <color attach="background" args={['#151719']} />
        <Lightformer
            color="#fff7ec"
            form="rect"
            intensity={5}
            position={[-5, 5, 4]}
            scale={[5, 7]}
            target={[0, 0, 0]}
        />
        <Lightformer
            color="#e8f1ff"
            form="rect"
            intensity={2}
            position={[5, 2, 4]}
            scale={[4, 5]}
            target={[0, 0, 0]}
        />
        <Lightformer
            color="#fffaf2"
            form="rect"
            intensity={3}
            position={[1, 5, -5]}
            scale={[3, 5]}
            target={[0, 0, 0]}
        />
        {reflectionStrength > 0 ? <>
            <Lightformer
                color={cycloramaColor}
                form="rect"
                intensity={
                    getCycloramaReflectionIntensity(
                        reflectionStrength,
                        BASE_CYCLORAMA_FLOOR_REFLECTION_INTENSITY,
                        MAX_CYCLORAMA_FLOOR_REFLECTION_INTENSITY,
                    )
                }
                position={[0, -4, 0]}
                scale={[12, 12]}
                target={[0, 0, 0]}
            />
            {CYCLORAMA_WALL_POSITIONS.map(position => <Lightformer
                key={position.join(':')}
                color={cycloramaColor}
                form="rect"
                intensity={
                    getCycloramaReflectionIntensity(
                        reflectionStrength,
                        BASE_CYCLORAMA_WALL_REFLECTION_INTENSITY,
                        MAX_CYCLORAMA_WALL_REFLECTION_INTENSITY,
                    )
                }
                position={position}
                scale={[20, 10]}
                target={[0, 0, 0]}
            />)}
        </> : null}
    </Environment>;
});
